/**
 * Theme Library for Flow2
 *
 * This file contains predefined style themes that map to existing FlowVella themes.
 * These themes are used by the AI compiler to style generated presentations.
 *
 * @packageDocumentation
 * @module ai/styles
 */

import { StyleTheme, createStyleTheme, ThemeColors } from '../schemas/StyleTheme';
import {
  ScreenDecoration,
  AbstractScreenDecoration,
  fullScreenBackground,
  bottomAccentBar,
  leftSidebar,
  pct,
  px,
} from './themeDecorations';

/**
 * FlowVella Theme ID to String ID mapping
 * Used for export compatibility
 */
export const FLOWVELLA_THEME_MAP: Record<string, string> = {
  '2': '9vu',
  '8': 'jv3',
  '10': 'dyl', // Default
  '18': '1s0g',
  '20': '20da',
  '22': '19at',
  '24': 'a1r',
  '26': '202t',
  '28': 'fc3',
  '30': '1s6i',
  '32': '1791',
  '34': 'lvv',
  '36': '1bkr',
  '38': 'hge',
  '42': '13wg',
  '44': 't0i',
  '46': '1soc',
  '48': 'kot',
  '50': 'ndz',
  '52': '1z5b',
  '54': 'u1y',
  '55': 'b0y',
  '56': '11gs',
  '57': '12gs',
  '58': '1xun',
  '59': 'n9f',
  '60': '1edc',
  '61': '15lg',
  '62': '19pu',
  '63': '1794',
  '64': '1iyv',
  '66': 'x44',
  '76': '108a',
  '77': '9js',
  '79': '238e',
  '83': 'rfl',
  '84': 'iea',
  '85': '22uv',
  '86': '18th',
  '87': '23ts',
  '88': '1cd',
  '89': 'eyx',
  '98': '18s7',
  '99': '1k2l',
  '100': '1luf',
  '101': '16jy',
  '102': 'clk',
  '103': 'may',
  '104': 'jcz',
  '106': '9x9',
  '115': '10dw',
  '133': 'hvy',
  '134': '18ry',
  '135': '1gc7',
  '136': '187r',
  '184': '3vh1',
  '185': '3wnz',
  '188': '2iwb',
  '189': '2vxe',
  '190': '3jeb',
  '191': '3csd',
  '192': '2wgm',
  '193': '31u5',
  '194': '3ga7',
  '195': '3oco',
  '196': '3un3',
  '197': '2ms4',
  '198': '2e6o',
  '199': '2qu7',
  '200': '3l16',
  '201': '3s5u',
  '202': '2r7y',
  '204': '3w67',
  '205': '3dpb',
};

/**
 * Clean Theme - THE DEFAULT
 * Ultra-minimal, fast-compiling theme optimized for the "happy path"
 * Pure white, subtle accents, minimal decorations = fast compile + clean look
 */
export const THEME_CLEAN: StyleTheme = createStyleTheme(
  'clean',
  'Clean',
  {
    primary: '#0F172A',
    secondary: '#334155',
    background: '#FFFFFF',
    textPrimary: '#0F172A',
    textSecondary: '#64748B',
    accent: ['#3B82F6', '#0F172A'],
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-Bold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.6,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'minimal',
    description: 'Ultra-clean, fast-loading theme. Pure white background with subtle accents. Perfect for any presentation.',
    tags: ['clean', 'minimal', 'fast', 'simple', 'white', 'default'],
    blockStyles: {
      title: {
        textColor: '#0F172A',
        fontWeight: 'bold',
      },
      subtitle: {
        textColor: '#64748B',
      },
      body: {
        textColor: '#334155',
      },
      quote: {
        textColor: '#64748B',
        fontName: 'Georgia-Italic',
      },
    },
    // Minimal decorations using auto-conform system = fast compile + works at any size
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          // White background (full screen)
          fullScreenBackground('#FFFFFF'),
          // Subtle gray footer bar
          bottomAccentBar('#F1F5F9', 38),
          // Small blue accent on bottom left
          {
            type: 'shape',
            anchor: 'bottom-left',
            width: px(120),
            height: px(8),
            offset: { x: 40, y: -10 },
            shapeType: 'rectangle',
            color: '#3B82F6',
          },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team', 'media'],
        elements: [
          // Pure white background
          fullScreenBackground('#FFFFFF'),
          // Thin left accent bar
          leftSidebar('#3B82F6', 4),
        ],
      },
    ] as AbstractScreenDecoration[],
    layoutPreferences: {
      // Favor layouts with 16:9 image slots (wide images)
      preferredLayoutIds: ['image-top', 'image-right', 'image-left', 'title-body', 'two-column'],
    },
  }
);

/**
 * Modern Dark Theme
 * Dark background with vibrant accents
 */
export const THEME_MODERN_DARK: StyleTheme = createStyleTheme(
  'modern-dark',
  'Modern Dark',
  {
    primary: '#6366F1',
    secondary: '#8B5CF6',
    background: '#1F2937',
    textPrimary: '#F9FAFB',
    textSecondary: '#9CA3AF',
    accent: ['#6366F1', '#8B5CF6', '#EC4899', '#10B981'],
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-Bold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.5,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'Best for tech startups, product launches, and innovation pitches with its dark backdrop and vibrant purple-pink accents that feel cutting-edge and forward-thinking.',
    tags: ['dark', 'modern', 'tech', 'startup'],
    blockStyles: {
      title: {
        textColor: '#F9FAFB',
        fontWeight: 'bold',
      },
      subtitle: {
        textColor: '#D1D5DB',
      },
      callout: {
        backgroundColor: '#374151',
        textColor: '#F9FAFB',
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#1F2937' },
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#6366F118', offset: 0 }, { color: '#8B5CF618', offset: 50 }, { color: '#EC489918', offset: 100 }] } },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#6366F1' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#1F2937' },
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 160, stops: [{ color: '#6366F10A', offset: 0 }, { color: '#1F293700', offset: 40 }, { color: '#EC48990A', offset: 100 }] } },
          { type: 'shape', frame: { x: 750, y: -80, width: 380, height: 380 }, shapeType: 'ellipse', color: '#6366F115' },
          { type: 'shape', frame: { x: -100, y: 500, width: 280, height: 280 }, shapeType: 'ellipse', color: '#8B5CF610' },
          { type: 'shape', frame: { x: 0, y: 0, width: 5, height: 748 }, shapeType: 'rectangle', color: '#6366F1' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#6366F1' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['image-text-overlay', 'image-top', 'full-image-content', 'image-bg-card'],
    },
  }
);

/**
 * Modern Light Theme
 * Clean white background with blue accents
 */
export const THEME_MODERN_LIGHT: StyleTheme = createStyleTheme(
  'modern-light',
  'Modern Light',
  {
    primary: '#3B82F6',
    secondary: '#6366F1',
    background: '#FFFFFF',
    textPrimary: '#111827',
    textSecondary: '#6B7280',
    accent: ['#3B82F6', '#6366F1', '#EC4899', '#10B981'],
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-Bold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.5,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A versatile light theme ideal for any general-purpose presentation, from team updates and project overviews to proposals and reports, with a clean professional feel.',
    tags: ['light', 'clean', 'modern', 'professional'],
    blockStyles: {
      title: {
        textColor: '#111827',
      },
      quote: {
        textColor: '#4B5563',
        fontName: 'Georgia-Italic',
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#F8FAFC' },
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#3B82F608', offset: 0 }, { color: '#6366F108', offset: 100 }] } },
          { type: 'shape', frame: { x: 80, y: 100, width: 864, height: 548 }, shapeType: 'roundedRectangle', color: '#FFFFFF90' },
          { type: 'shape', frame: { x: 700, y: -60, width: 400, height: 400 }, shapeType: 'ellipse', color: '#3B82F60A' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#3B82F6' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 180, stops: [{ color: '#F8FAFC', offset: 0 }, { color: '#EFF6FF', offset: 100 }] } },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 6 }, shapeType: 'rectangle', color: '#3B82F6' },
          { type: 'shape', frame: { x: 800, y: -120, width: 340, height: 340 }, shapeType: 'ellipse', color: '#3B82F60A' },
          { type: 'shape', frame: { x: -60, y: 550, width: 200, height: 200 }, shapeType: 'ellipse', color: '#6366F108' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#EFF6FF' },
          { type: 'shape', frame: { x: 0, y: 720, width: 200, height: 28 }, shapeType: 'rectangle', color: '#3B82F6' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['image-top', 'image-right', 'image-left-wide', 'image-right-wide'],
    },
  }
);

/**
 * Professional Theme
 * Navy and gold corporate look
 */
export const THEME_PROFESSIONAL: StyleTheme = createStyleTheme(
  'professional',
  'Professional',
  {
    primary: '#1E3A5F',
    secondary: '#2E5A88',
    background: '#FAFAFA',
    textPrimary: '#1E3A5F',
    textSecondary: '#4A5568',
    accent: ['#D4AF37', '#1E3A5F', '#2E5A88', '#4A7C94'],
    success: '#2F855A',
    warning: '#C27803',
    error: '#C53030',
  },
  {
    typography: {
      titleFont: 'Georgia-Bold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.6,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'professional',
    description: 'A polished navy and gold theme suited for board meetings, investor updates, corporate strategy decks, and executive briefings that demand authority and trust.',
    tags: ['professional', 'corporate', 'business', 'formal'],
    blockStyles: {
      title: {
        textColor: '#1E3A5F',
        fontName: 'Georgia-Bold',
      },
      callout: {
        backgroundColor: '#E6E9ED',
        textColor: '#1E3A5F',
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#1E3A5F' },
          { type: 'gradient-shape', frame: { x: 300, y: 200, width: 500, height: 500 }, shapeType: 'ellipse', gradient: { type: 'radial', stops: [{ color: '#D4AF3718', offset: 0 }, { color: '#1E3A5F00', offset: 70 }] } },
          { type: 'shape', frame: { x: 60, y: 60, width: 904, height: 1 }, shapeType: 'rectangle', color: '#D4AF3740' },
          { type: 'shape', frame: { x: 60, y: 688, width: 904, height: 1 }, shapeType: 'rectangle', color: '#D4AF3740' },
          { type: 'shape', frame: { x: 0, y: 700, width: 1024, height: 48 }, shapeType: 'rectangle', color: '#D4AF37' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 6 }, shapeType: 'rectangle', color: '#1E3A5F' },
          { type: 'shape', frame: { x: 0, y: 6, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#D4AF37' },
          { type: 'gradient-shape', frame: { x: 700, y: 200, width: 400, height: 400 }, shapeType: 'ellipse', gradient: { type: 'radial', stops: [{ color: '#D4AF370A', offset: 0 }, { color: '#FAFAFA00', offset: 70 }] } },
          { type: 'shape', frame: { x: 0, y: 700, width: 1024, height: 48 }, shapeType: 'rectangle', color: '#1E3A5F' },
          { type: 'shape', frame: { x: 60, y: 712, width: 200, height: 2 }, shapeType: 'rectangle', color: '#D4AF37' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['two-column', 'title-body', 'image-right-wide'],
      screenTypeLayouts: { 'comparison': 'three-column' },
    },
  }
);

/**
 * Creative Theme
 * Bold colors and playful design
 */
export const THEME_CREATIVE: StyleTheme = createStyleTheme(
  'creative',
  'Creative',
  {
    primary: '#FF6B6B',
    secondary: '#4ECDC4',
    background: '#FFF9F0',
    textPrimary: '#2D3436',
    textSecondary: '#636E72',
    accent: ['#FF6B6B', '#4ECDC4', '#A29BFE', '#FD79A8'],
    success: '#00B894',
    warning: '#FDCB6E',
    error: '#D63031',
  },
  {
    typography: {
      titleFont: 'Futura-Medium',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.5,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'A playful, energetic theme with bold colors perfect for creative agencies, design portfolios, brainstorming sessions, and marketing campaigns that want to stand out.',
    tags: ['creative', 'colorful', 'fun', 'design'],
    blockStyles: {
      title: {
        textColor: '#2D3436',
        fontSize: 70,
      },
      body: {
        fontSize: 25,
      },
      callout: {
        backgroundColor: '#FF6B6B',
        textColor: '#FFFFFF',
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#FF6B6B20', offset: 0 }, { color: '#4ECDC420', offset: 50 }, { color: '#A29BFE20', offset: 100 }] } },
          { type: 'shape', frame: { x: 0, y: 720, width: 340, height: 28 }, shapeType: 'rectangle', color: '#FF6B6B' },
          { type: 'shape', frame: { x: 342, y: 720, width: 340, height: 28 }, shapeType: 'rectangle', color: '#4ECDC4' },
          { type: 'shape', frame: { x: 684, y: 720, width: 340, height: 28 }, shapeType: 'rectangle', color: '#A29BFE' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#FF6B6B08', offset: 0 }, { color: '#FFF9F0', offset: 50 }, { color: '#4ECDC408', offset: 100 }] } },
          { type: 'shape', frame: { x: 800, y: -60, width: 300, height: 300 }, shapeType: 'ellipse', color: '#FF6B6B0A' },
          { type: 'shape', frame: { x: -80, y: 500, width: 220, height: 220 }, shapeType: 'ellipse', color: '#4ECDC40A' },
          { type: 'shape', frame: { x: 0, y: 720, width: 340, height: 28 }, shapeType: 'rectangle', color: '#FF6B6B' },
          { type: 'shape', frame: { x: 342, y: 720, width: 340, height: 28 }, shapeType: 'rectangle', color: '#4ECDC4' },
          { type: 'shape', frame: { x: 684, y: 720, width: 340, height: 28 }, shapeType: 'rectangle', color: '#A29BFE' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['image-grid', 'image-top', 'two-images', 'full-image-content', 'image-left-wide'],
    },
  }
);

/**
 * Minimal Theme
 * Ultra-clean with lots of whitespace
 */
export const THEME_MINIMAL: StyleTheme = createStyleTheme(
  'minimal',
  'Minimal',
  {
    primary: '#000000',
    secondary: '#333333',
    background: '#FFFFFF',
    textPrimary: '#000000',
    textSecondary: '#666666',
    accent: ['#000000', '#333333', '#666666', '#999999'],
    success: '#000000',
    warning: '#666666',
    error: '#333333',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-Light',
      bodyFont: 'HelveticaNeue-Light',
      baseFontSize: 28,
      titleWeight: 'light',
      bodyWeight: 'light',
      lineHeight: 1.7,
    },
    spacing: {
      unit: 12,
      screenPadding: 60,
      elementGap: 30,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'minimal',
    description: 'A stripped-down black and white theme for content that speaks for itself, ideal for text-heavy reports, data presentations, minimalist portfolios, and academic talks.',
    tags: ['minimal', 'clean', 'simple', 'elegant'],
    blockStyles: {
      title: {
        fontWeight: 'light',
        letterSpacing: 2,
      },
      body: {
        lineHeight: 1.8,
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 80, y: 680, width: 864, height: 1 }, shapeType: 'rectangle', color: '#000000' },
          { type: 'shape', frame: { x: 80, y: 672, width: 6, height: 6 }, shapeType: 'ellipse', color: '#000000' },
          { type: 'shape', frame: { x: 100, y: 100, width: 824, height: 560 }, shapeType: 'rectangle', color: '#00000006' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 80, y: 60, width: 864, height: 1 }, shapeType: 'rectangle', color: '#00000020' },
          { type: 'shape', frame: { x: 80, y: 52, width: 6, height: 6 }, shapeType: 'ellipse', color: '#00000020' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['title-centered', 'full-image'],
    },
  }
);

/**
 * Gradient Blue Theme
 * Blue gradient backgrounds
 */
export const THEME_GRADIENT_BLUE: StyleTheme = createStyleTheme(
  'gradient-blue',
  'Gradient Blue',
  {
    primary: '#667EEA',
    secondary: '#764BA2',
    background: '#667EEA',
    textPrimary: '#FFFFFF',
    textSecondary: '#E0E7FF',
    accent: ['#667EEA', '#764BA2', '#F093FB', '#F5576C'],
    success: '#68D391',
    warning: '#F6E05E',
    error: '#FC8181',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-Bold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.5,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'A striking blue-to-purple gradient theme that feels dynamic and modern, great for product demos, SaaS pitches, social media content, and app showcases.',
    tags: ['gradient', 'blue', 'purple', 'vibrant'],
    blockStyles: {
      title: {
        textColor: '#FFFFFF',
      },
      subtitle: {
        textColor: '#E0E7FF',
      },
      callout: {
        backgroundColor: 'rgba(255,255,255,0.2)',
        textColor: '#FFFFFF',
      },
    },
    decorations: [
      {
        // All screens get the gradient background
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#667EEA', offset: 0 }, { color: '#764BA2', offset: 100 }] } },
        ],
      },
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 100, y: 150, width: 824, height: 450 }, shapeType: 'roundedRectangle', color: '#FFFFFF15' },
        ],
      },
    ],
  }
);

/**
 * Nature Theme
 * Earthy greens and browns
 */
export const THEME_NATURE: StyleTheme = createStyleTheme(
  'nature',
  'Nature',
  {
    primary: '#2D5A27',
    secondary: '#5D8C56',
    background: '#F5F5DC',
    textPrimary: '#2D3319',
    textSecondary: '#4A5240',
    accent: ['#2D5A27', '#5D8C56', '#8B4513', '#D2691E'],
    success: '#228B22',
    warning: '#DAA520',
    error: '#8B0000',
  },
  {
    typography: {
      titleFont: 'Georgia-Bold',
      bodyFont: 'Georgia',
      baseFontSize: 28,
      lineHeight: 1.6,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A calming nature-inspired theme with earthy greens and warm tones, perfect for sustainability reports, environmental topics, outdoor brands, and wellness content.',
    tags: ['nature', 'green', 'organic', 'earth'],
    blockStyles: {
      title: {
        textColor: '#2D5A27',
      },
      quote: {
        fontName: 'Georgia-Italic',
        textColor: '#4A5240',
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          // Nature gradient background - earthy tones
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#2D5A27', offset: 0 }, { color: '#4A7C40', offset: 50 }, { color: '#F5F5DC', offset: 100 }] } },
          { type: 'shape', frame: { x: 80, y: 200, width: 500, height: 350 }, shapeType: 'roundedRectangle', color: '#FFFFFF90' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 6, height: 748 }, shapeType: 'rectangle', color: '#2D5A27' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['full-image', 'image-top', 'image-text-overlay', 'image-left-wide', 'image-bottom'],
    },
  }
);

/**
 * Tech Theme
 * Neon accents on dark background
 */
export const THEME_TECH: StyleTheme = createStyleTheme(
  'tech',
  'Tech',
  {
    primary: '#00D4FF',
    secondary: '#7B61FF',
    background: '#0A0A0F',
    textPrimary: '#FFFFFF',
    textSecondary: '#A0AEC0',
    accent: ['#00D4FF', '#7B61FF', '#FF0080', '#00FF87'],
    success: '#00FF87',
    warning: '#FFCC00',
    error: '#FF3366',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-Bold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.5,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'A futuristic cyberpunk-inspired theme with neon cyan accents on dark backgrounds, ideal for AI, cybersecurity, gaming, and cutting-edge technology presentations.',
    tags: ['tech', 'neon', 'dark', 'futuristic', 'cyber'],
    blockStyles: {
      title: {
        textColor: '#00D4FF',
      },
      callout: {
        backgroundColor: '#1A1A2E',
        textColor: '#00D4FF',
        borderRadius: 4,
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0A0A0F' },
          { type: 'gradient-shape', frame: { x: 0, y: 500, width: 1024, height: 268 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 0, stops: [{ color: '#00D4FF00', offset: 0 }, { color: '#00D4FF20', offset: 100 }] } },
          { type: 'shape', frame: { x: 0, y: 740, width: 1024, height: 8 }, shapeType: 'rectangle', color: '#00D4FF' },
          { type: 'shape', frame: { x: 750, y: -50, width: 350, height: 350 }, shapeType: 'ellipse', color: '#7B61FF18' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0A0A0F' },
          { type: 'gradient-shape', frame: { x: 0, y: 500, width: 1024, height: 268 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 0, stops: [{ color: '#00D4FF00', offset: 0 }, { color: '#00D4FF10', offset: 100 }] } },
          { type: 'shape', frame: { x: 750, y: -80, width: 350, height: 350 }, shapeType: 'ellipse', color: '#7B61FF10' },
          { type: 'shape', frame: { x: -60, y: 400, width: 200, height: 200 }, shapeType: 'ellipse', color: '#00D4FF08' },
          { type: 'shape', frame: { x: 0, y: 0, width: 3, height: 748 }, shapeType: 'rectangle', color: '#00D4FF' },
          { type: 'shape', frame: { x: 0, y: 740, width: 1024, height: 8 }, shapeType: 'rectangle', color: '#00D4FF' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['full-image-content', 'image-text-overlay', 'image-top'],
    },
  }
);

/**
 * Warm Theme
 * Warm orange and red tones
 */
export const THEME_WARM: StyleTheme = createStyleTheme(
  'warm',
  'Warm',
  {
    primary: '#E64A19',
    secondary: '#FF7043',
    background: '#FFF8E1',
    textPrimary: '#3E2723',
    textSecondary: '#5D4037',
    accent: ['#E64A19', '#FF7043', '#FFAB91', '#FFE0B2'],
    success: '#4CAF50',
    warning: '#FFC107',
    error: '#D32F2F',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-Bold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.5,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'A warm, welcoming theme with orange accents that feels approachable and friendly, great for community events, team introductions, onboarding, and hospitality.',
    tags: ['warm', 'orange', 'friendly', 'inviting'],
    blockStyles: {
      title: {
        textColor: '#E64A19',
      },
      callout: {
        backgroundColor: '#FFE0B2',
        textColor: '#3E2723',
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 160, stops: [{ color: '#FFF8E1', offset: 0 }, { color: '#FFE0B2', offset: 100 }] } },
          { type: 'shape', frame: { x: 0, y: 730, width: 1024, height: 38 }, shapeType: 'rectangle', color: '#E64A19' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 180, stops: [{ color: '#FFF8E1', offset: 0 }, { color: '#FFE0B2', offset: 100 }] } },
          { type: 'shape', frame: { x: 800, y: -60, width: 300, height: 300 }, shapeType: 'ellipse', color: '#E64A190A' },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 4 }, shapeType: 'rectangle', color: '#E64A19' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#E64A19' },
        ],
      },
    ],
  }
);

/**
 * Ocean Theme
 * Cool blues and teals
 */
export const THEME_OCEAN: StyleTheme = createStyleTheme(
  'ocean',
  'Ocean',
  {
    primary: '#0077B6',
    secondary: '#00B4D8',
    background: '#CAF0F8',
    textPrimary: '#03045E',
    textSecondary: '#0077B6',
    accent: ['#03045E', '#0077B6', '#00B4D8', '#90E0EF'],
    success: '#2A9D8F',
    warning: '#E9C46A',
    error: '#E76F51',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-Bold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.5,
    },
    flowvellaThemeId: '10',
    flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A serene ocean-inspired theme with cool blues and teals that evokes calm and clarity, ideal for travel, marine topics, meditation, and relaxation-focused content.',
    tags: ['ocean', 'blue', 'calm', 'water', 'cool'],
    blockStyles: {
      title: {
        textColor: '#03045E',
      },
      quote: {
        textColor: '#0077B6',
        fontName: 'Georgia-Italic',
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 180, stops: [{ color: '#CAF0F8', offset: 0 }, { color: '#90E0EF', offset: 100 }] } },
          { type: 'gradient-shape', frame: { x: -100, y: 400, width: 500, height: 500 }, shapeType: 'ellipse', gradient: { type: 'radial', stops: [{ color: '#0077B615', offset: 0 }, { color: '#CAF0F800', offset: 70 }] } },
          { type: 'shape', frame: { x: 80, y: 100, width: 864, height: 548 }, shapeType: 'roundedRectangle', color: '#FFFFFF60' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#0077B6' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 4 }, shapeType: 'rectangle', color: '#0077B6' },
          { type: 'shape', frame: { x: 60, y: 40, width: 904, height: 668 }, shapeType: 'roundedRectangle', color: '#FFFFFF40' },
        ],
      },
    ],
  }
);

// ============================================================
// Professional Variations
// ============================================================

export const THEME_CORPORATE_BLUE: StyleTheme = createStyleTheme(
  'corporate-blue',
  'Corporate Blue',
  {
    primary: '#2B6CB0',
    secondary: '#3182CE',
    background: '#F0F4F8',
    textPrimary: '#1A202C',
    textSecondary: '#4A5568',
    accent: ['#2B6CB0', '#3182CE', '#63B3ED', '#BEE3F8'],
    success: '#38A169',
    warning: '#D69E2E',
    error: '#E53E3E',
  },
  {
    typography: { titleFont: 'Montserrat-Bold', bodyFont: 'OpenSans' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'professional',
    description: 'A trustworthy corporate blue theme designed for quarterly business reviews, company updates, sales presentations, and professional proposals.',
    tags: ['corporate', 'blue', 'business', 'professional'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#1E3A5F' },
          { type: 'shape', frame: { x: 60, y: 60, width: 904, height: 648 }, shapeType: 'roundedRectangle', color: '#FFFFFF10' },
          { type: 'shape', frame: { x: 0, y: 730, width: 1024, height: 38 }, shapeType: 'rectangle', color: '#2563EB' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#F8FAFC' },
          { type: 'shape', frame: { x: 40, y: 40, width: 944, height: 688 }, shapeType: 'roundedRectangle', color: '#FFFFFF' },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 4 }, shapeType: 'rectangle', color: '#2563EB' },
        ],
      },
    ],
  }
);

export const THEME_EXECUTIVE: StyleTheme = createStyleTheme(
  'executive',
  'Executive',
  {
    primary: '#44403C',
    secondary: '#78716C',
    background: '#FAFAF9',
    textPrimary: '#1C1917',
    textSecondary: '#57534E',
    accent: ['#44403C', '#78716C', '#A8A29E', '#D6D3D1'],
    success: '#15803D',
    warning: '#A16207',
    error: '#B91C1C',
  },
  {
    typography: { titleFont: 'Georgia-Bold', bodyFont: 'Lato-Regular', lineHeight: 1.6 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'professional',
    description: 'An understated executive theme with warm neutrals and serif typography, suited for C-suite presentations, leadership talks, and high-level strategic discussions.',
    tags: ['executive', 'neutral', 'business', 'formal'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#292524' },
          { type: 'gradient-shape', frame: { x: 200, y: 150, width: 600, height: 600 }, shapeType: 'ellipse', gradient: { type: 'radial', stops: [{ color: '#78716C15', offset: 0 }, { color: '#29252400', offset: 70 }] } },
          { type: 'shape', frame: { x: 80, y: 80, width: 864, height: 588 }, shapeType: 'roundedRectangle', color: '#44403C20' },
          { type: 'shape', frame: { x: 0, y: 710, width: 1024, height: 38 }, shapeType: 'rectangle', color: '#44403C' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#44403C' },
          { type: 'shape', frame: { x: 60, y: 40, width: 904, height: 668 }, shapeType: 'roundedRectangle', color: '#44403C08' },
        ],
      },
    ],
  }
);

export const THEME_CONSULTING: StyleTheme = createStyleTheme(
  'consulting',
  'Consulting',
  {
    primary: '#0F172A',
    secondary: '#334155',
    background: '#FFFFFF',
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    accent: ['#0F172A', '#334155', '#64748B', '#CBD5E1'],
    success: '#059669',
    warning: '#D97706',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'Roboto-Bold', bodyFont: 'Roboto-Light' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'professional',
    description: 'A sharp, high-contrast consulting theme with clean lines and modern sans-serif fonts, ideal for management consulting decks, strategy frameworks, and analytical presentations.',
    tags: ['consulting', 'sharp', 'clean', 'professional'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0F172A' },
          { type: 'shape', frame: { x: 60, y: 60, width: 904, height: 628 }, shapeType: 'roundedRectangle', color: '#1E293B60' },
          { type: 'shape', frame: { x: 0, y: 740, width: 1024, height: 8 }, shapeType: 'rectangle', color: '#334155' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 4 }, shapeType: 'rectangle', color: '#0F172A' },
        ],
      },
    ],
  }
);

// ============================================================
// Education
// ============================================================

export const THEME_CLASSROOM: StyleTheme = createStyleTheme(
  'classroom',
  'Classroom',
  {
    primary: '#EA580C',
    secondary: '#F97316',
    background: '#FFFBEB',
    textPrimary: '#1C1917',
    textSecondary: '#78716C',
    accent: ['#EA580C', '#F97316', '#FBBF24', '#FDE68A'],
    success: '#16A34A',
    warning: '#CA8A04',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'HelveticaNeue-Bold', bodyFont: 'OpenSans' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A warm, engaging theme with inviting colors designed for classroom lessons, training materials, tutorials, workshops, and educational content for students.',
    tags: ['classroom', 'education', 'warm', 'teaching'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 160, stops: [{ color: '#FFFBEB', offset: 0 }, { color: '#FDE68A40', offset: 100 }] } },
          { type: 'shape', frame: { x: 80, y: 100, width: 864, height: 548 }, shapeType: 'roundedRectangle', color: '#FFFFFF80' },
          { type: 'shape', frame: { x: 750, y: -50, width: 300, height: 300 }, shapeType: 'ellipse', color: '#FBBF2415' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#EA580C' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', color: '#EA580C' },
          { type: 'shape', frame: { x: 40, y: 30, width: 944, height: 688 }, shapeType: 'roundedRectangle', color: '#FDE68A10' },
        ],
      },
    ],
  }
);

export const THEME_ACADEMIC: StyleTheme = createStyleTheme(
  'academic',
  'Academic',
  {
    primary: '#7C3AED',
    secondary: '#8B5CF6',
    background: '#FFF7ED',
    textPrimary: '#1E1B4B',
    textSecondary: '#4C1D95',
    accent: ['#7C3AED', '#8B5CF6', '#A78BFA', '#DDD6FE'],
    success: '#059669',
    warning: '#D97706',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'Georgia-Bold', bodyFont: 'Georgia', lineHeight: 1.6 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'professional',
    description: 'A scholarly theme with classic serif fonts and a refined color palette, perfect for university lectures, research presentations, thesis defenses, and academic conferences.',
    tags: ['academic', 'research', 'scholarly', 'university'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#1E1B4B' },
          { type: 'shape', frame: { x: 60, y: 60, width: 904, height: 628 }, shapeType: 'roundedRectangle', color: '#FFF7ED' },
          { type: 'shape', frame: { x: 0, y: 740, width: 1024, height: 8 }, shapeType: 'rectangle', color: '#7C3AED' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 4 }, shapeType: 'rectangle', color: '#7C3AED' },
        ],
      },
    ],
  }
);

export const THEME_SCIENCE: StyleTheme = createStyleTheme(
  'science',
  'Science',
  {
    primary: '#16A34A',
    secondary: '#22C55E',
    background: '#F0FDF4',
    textPrimary: '#14532D',
    textSecondary: '#166534',
    accent: ['#16A34A', '#22C55E', '#4ADE80', '#BBF7D0'],
    success: '#16A34A',
    warning: '#EAB308',
    error: '#EF4444',
  },
  {
    typography: { titleFont: 'Roboto-Bold', bodyFont: 'Roboto-Regular' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A fresh green science theme for STEM topics, lab results, biology, chemistry, environmental science, and data-driven research findings.',
    tags: ['science', 'green', 'stem', 'research'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#F0FDF4', offset: 0 }, { color: '#BBF7D030', offset: 100 }] } },
          // Grid-like inset frame
          { type: 'shape', frame: { x: 60, y: 60, width: 904, height: 1 }, shapeType: 'rectangle', color: '#16A34A15' },
          { type: 'shape', frame: { x: 60, y: 688, width: 904, height: 1 }, shapeType: 'rectangle', color: '#16A34A15' },
          { type: 'shape', frame: { x: 60, y: 60, width: 1, height: 628 }, shapeType: 'rectangle', color: '#16A34A15' },
          { type: 'shape', frame: { x: 963, y: 60, width: 1, height: 628 }, shapeType: 'rectangle', color: '#16A34A15' },
          { type: 'shape', frame: { x: 950, y: 52, width: 8, height: 8 }, shapeType: 'ellipse', color: '#16A34A30' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#16A34A' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#16A34A' },
          { type: 'shape', frame: { x: 60, y: 40, width: 1, height: 668 }, shapeType: 'rectangle', color: '#16A34A10' },
          { type: 'shape', frame: { x: 963, y: 40, width: 1, height: 668 }, shapeType: 'rectangle', color: '#16A34A10' },
        ],
      },
    ],
  }
);

// ============================================================
// Creative/Design
// ============================================================

export const THEME_PORTFOLIO: StyleTheme = createStyleTheme(
  'portfolio',
  'Portfolio',
  {
    primary: '#F43F5E',
    secondary: '#FB7185',
    background: '#18181B',
    textPrimary: '#FAFAFA',
    textSecondary: '#A1A1AA',
    accent: ['#F43F5E', '#FB7185', '#FDA4AF', '#FECDD3'],
    success: '#22C55E',
    warning: '#EAB308',
    error: '#EF4444',
  },
  {
    typography: { titleFont: 'Futura-Medium', bodyFont: 'HelveticaNeue-Light' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'A sleek dark portfolio theme with bold rose accents, built for designers, photographers, and creatives showcasing their work with dramatic visual impact.',
    tags: ['portfolio', 'dark', 'creative', 'design'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#18181B' },
          { type: 'shape', frame: { x: 60, y: 100, width: 904, height: 550 }, shapeType: 'roundedRectangle', color: '#27272A60' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#F43F5E' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'image-focus'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#18181B' },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#F43F5E' },
        ],
      },
    ],
  }
);

export const THEME_GALLERY: StyleTheme = createStyleTheme(
  'gallery',
  'Gallery',
  {
    primary: '#A3A3A3',
    secondary: '#D4D4D4',
    background: '#FFFFFF',
    textPrimary: '#171717',
    textSecondary: '#737373',
    accent: ['#A3A3A3', '#D4D4D4', '#E5E5E5', '#F5F5F5'],
    success: '#22C55E',
    warning: '#EAB308',
    error: '#EF4444',
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-UltraLight',
      bodyFont: 'HelveticaNeue-Light',
      titleWeight: 'light',
      lineHeight: 1.7,
    },
    spacing: { unit: 12, screenPadding: 60, elementGap: 30 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'minimal',
    description: 'An ultra-clean gallery-style theme with generous whitespace, made for photography portfolios, art exhibitions, product showcases, and visual storytelling.',
    tags: ['gallery', 'minimal', 'clean', 'photography'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          // L-shaped corner brackets (top-left)
          { type: 'shape', frame: { x: 60, y: 60, width: 40, height: 2 }, shapeType: 'rectangle', color: '#A3A3A3' },
          { type: 'shape', frame: { x: 60, y: 60, width: 2, height: 40 }, shapeType: 'rectangle', color: '#A3A3A3' },
          // L-shaped corner brackets (top-right)
          { type: 'shape', frame: { x: 924, y: 60, width: 40, height: 2 }, shapeType: 'rectangle', color: '#A3A3A3' },
          { type: 'shape', frame: { x: 962, y: 60, width: 2, height: 40 }, shapeType: 'rectangle', color: '#A3A3A3' },
          // L-shaped corner brackets (bottom-left)
          { type: 'shape', frame: { x: 60, y: 688, width: 40, height: 2 }, shapeType: 'rectangle', color: '#A3A3A3' },
          { type: 'shape', frame: { x: 60, y: 650, width: 2, height: 40 }, shapeType: 'rectangle', color: '#A3A3A3' },
          // L-shaped corner brackets (bottom-right)
          { type: 'shape', frame: { x: 924, y: 688, width: 40, height: 2 }, shapeType: 'rectangle', color: '#A3A3A3' },
          { type: 'shape', frame: { x: 962, y: 650, width: 2, height: 40 }, shapeType: 'rectangle', color: '#A3A3A3' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          // Subtle top-left bracket only
          { type: 'shape', frame: { x: 60, y: 40, width: 30, height: 1 }, shapeType: 'rectangle', color: '#A3A3A360' },
          { type: 'shape', frame: { x: 60, y: 40, width: 1, height: 30 }, shapeType: 'rectangle', color: '#A3A3A360' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['full-image', 'image-grid', 'image-text-overlay'],
      screenTypeLayouts: { 'image-focus': 'full-image' },
    },
  }
);

export const THEME_BOLD_TYPE: StyleTheme = createStyleTheme(
  'bold-type',
  'Bold Typography',
  {
    primary: '#0F172A',
    secondary: '#1E293B',
    background: '#FEF08A',
    textPrimary: '#0F172A',
    textSecondary: '#334155',
    accent: ['#0F172A', '#1E293B', '#475569', '#94A3B8'],
    success: '#16A34A',
    warning: '#CA8A04',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'LeagueGothic-Regular', bodyFont: 'OpenSans' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'A high-impact typographic theme with a striking yellow backdrop, designed for attention-grabbing headlines, motivational talks, advertisements, and bold statements.',
    tags: ['bold', 'typography', 'yellow', 'impactful'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0F172A' },
          { type: 'shape', frame: { x: 40, y: 40, width: 500, height: 300 }, shapeType: 'rectangle', color: '#FEF08A20' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 6 }, shapeType: 'rectangle', color: '#0F172A' },
          { type: 'shape', frame: { x: 900, y: 600, width: 100, height: 120 }, shapeType: 'rectangle', color: '#0F172A10' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['title-centered', 'title-body', 'full-image-content'],
    },
  }
);

// ============================================================
// Industry
// ============================================================

export const THEME_HEALTHCARE: StyleTheme = createStyleTheme(
  'healthcare',
  'Healthcare',
  {
    primary: '#0D9488',
    secondary: '#14B8A6',
    background: '#F0FDFA',
    textPrimary: '#134E4A',
    textSecondary: '#115E59',
    accent: ['#0D9488', '#14B8A6', '#5EEAD4', '#99F6E4'],
    success: '#059669',
    warning: '#D97706',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'HelveticaNeue-Bold', bodyFont: 'HelveticaNeue' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A calming teal healthcare theme that conveys trust and care, ideal for medical presentations, hospital reports, health and wellness programs, and pharmaceutical content.',
    tags: ['healthcare', 'medical', 'teal', 'wellness'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 160, stops: [{ color: '#F0FDFA', offset: 0 }, { color: '#99F6E430', offset: 100 }] } },
          { type: 'shape', frame: { x: 80, y: 100, width: 864, height: 548 }, shapeType: 'roundedRectangle', color: '#FFFFFF70' },
          { type: 'gradient-shape', frame: { x: 700, y: 400, width: 400, height: 400 }, shapeType: 'ellipse', gradient: { type: 'radial', stops: [{ color: '#0D948812', offset: 0 }, { color: '#F0FDFA00', offset: 70 }] } },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#0D9488' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', color: '#0D9488' },
          { type: 'shape', frame: { x: 40, y: 30, width: 944, height: 688 }, shapeType: 'roundedRectangle', color: '#FFFFFF40' },
        ],
      },
    ],
  }
);

export const THEME_REAL_ESTATE: StyleTheme = createStyleTheme(
  'real-estate',
  'Real Estate',
  {
    primary: '#9F1239',
    secondary: '#BE123C',
    background: '#FDF2F8',
    textPrimary: '#4C0519',
    textSecondary: '#881337',
    accent: ['#9F1239', '#BE123C', '#FB7185', '#FECDD3'],
    success: '#059669',
    warning: '#D97706',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'Montserrat-Bold', bodyFont: 'Lato-Regular', lineHeight: 1.6 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'professional',
    description: 'A premium theme with rose accents and elegant typography, tailored for real estate listings, luxury property showcases, architecture firms, and interior design portfolios.',
    tags: ['real-estate', 'luxury', 'premium', 'property'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#4C0519' },
          { type: 'shape', frame: { x: 60, y: 100, width: 904, height: 548 }, shapeType: 'roundedRectangle', color: '#FDF2F8' },
          { type: 'shape', frame: { x: 0, y: 710, width: 1024, height: 38 }, shapeType: 'rectangle', color: '#9F1239' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 4 }, shapeType: 'rectangle', color: '#9F1239' },
        ],
      },
    ],
  }
);

export const THEME_FINANCE: StyleTheme = createStyleTheme(
  'finance',
  'Finance',
  {
    primary: '#1D4ED8',
    secondary: '#2563EB',
    background: '#F8FAFC',
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    accent: ['#1D4ED8', '#2563EB', '#60A5FA', '#BFDBFE'],
    success: '#059669',
    warning: '#D97706',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'Georgia-Bold', bodyFont: 'Roboto-Regular' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'professional',
    description: 'A reliable blue-accented theme that conveys stability and trust, perfect for financial reports, banking presentations, investment pitches, and economic analysis.',
    tags: ['finance', 'banking', 'blue', 'trust'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0F172A' },
          { type: 'shape', frame: { x: 0, y: 700, width: 1024, height: 68 }, shapeType: 'rectangle', color: '#1D4ED8' },
          { type: 'shape', frame: { x: 60, y: 80, width: 904, height: 580 }, shapeType: 'roundedRectangle', color: '#1E293B60' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 4 }, shapeType: 'rectangle', color: '#1D4ED8' },
        ],
      },
    ],
  }
);

export const THEME_STARTUP: StyleTheme = createStyleTheme(
  'startup',
  'Startup',
  {
    primary: '#8B5CF6',
    secondary: '#A78BFA',
    background: '#0F172A',
    textPrimary: '#F8FAFC',
    textSecondary: '#94A3B8',
    accent: ['#8B5CF6', '#A78BFA', '#C4B5FD', '#EDE9FE'],
    success: '#22C55E',
    warning: '#EAB308',
    error: '#EF4444',
  },
  {
    typography: { titleFont: 'Montserrat-Bold', bodyFont: 'OpenSans' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'A bold dark theme with purple accents built for startup pitch decks, venture capital presentations, product roadmaps, and entrepreneurship talks.',
    tags: ['startup', 'dark', 'purple', 'venture'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          // Startup gradient - dark with purple energy
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#0F172A', offset: 0 }, { color: '#1E1B4B', offset: 50 }, { color: '#312E81', offset: 100 }] } },
          { type: 'shape', frame: { x: 80, y: 120, width: 864, height: 500 }, shapeType: 'roundedRectangle', color: '#1E293B80' },
          { type: 'shape', frame: { x: 650, y: -80, width: 500, height: 500 }, shapeType: 'ellipse', color: '#8B5CF630' },
        ],
      },
      {
        // All screens: base + glow + accent bar
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0F172A' },
          { type: 'shape', frame: { x: 700, y: -100, width: 450, height: 450 }, shapeType: 'ellipse', color: '#8B5CF625' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#8B5CF6' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['image-top', 'full-image', 'image-text-overlay', 'full-image-content', 'image-bg-card'],
      screenTypeLayouts: { 'title-slide': 'full-image' },
    },
  }
);

// ============================================================
// Events
// ============================================================

export const THEME_CONFERENCE: StyleTheme = createStyleTheme(
  'conference',
  'Conference',
  {
    primary: '#6366F1',
    secondary: '#818CF8',
    background: '#1E1B4B',
    textPrimary: '#E0E7FF',
    textSecondary: '#A5B4FC',
    accent: ['#6366F1', '#818CF8', '#A5B4FC', '#C7D2FE'],
    success: '#34D399',
    warning: '#FBBF24',
    error: '#F87171',
  },
  {
    typography: { titleFont: 'Montserrat-Bold', bodyFont: 'Roboto-Regular' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'A deep indigo theme designed for conference keynotes, tech summits, speaker sessions, and large event presentations that need a professional stage presence.',
    tags: ['conference', 'event', 'indigo', 'speaker'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          // Conference gradient - deep indigo with stage presence
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 180, stops: [{ color: '#312E81', offset: 0 }, { color: '#1E1B4B', offset: 40 }, { color: '#0F0D2E', offset: 100 }] } },
          { type: 'gradient-shape', frame: { x: 0, y: 550, width: 1024, height: 218 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 0, stops: [{ color: '#6366F100', offset: 0 }, { color: '#6366F140', offset: 100 }] } },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#1E1B4B' },
          { type: 'shape', frame: { x: 0, y: 0, width: 5, height: 748 }, shapeType: 'rectangle', color: '#6366F1' },
          { type: 'shape', frame: { x: 60, y: 80, width: 904, height: 608 }, shapeType: 'roundedRectangle', color: '#1E1B4B40' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['title-centered', 'full-image', 'image-text-overlay', 'full-image-content', 'image-bg-card'],
      screenTypeLayouts: { 'title-slide': 'title-centered' },
    },
  }
);

export const THEME_WORKSHOP: StyleTheme = createStyleTheme(
  'workshop',
  'Workshop',
  {
    primary: '#059669',
    secondary: '#10B981',
    background: '#ECFDF5',
    textPrimary: '#064E3B',
    textSecondary: '#065F46',
    accent: ['#059669', '#10B981', '#6EE7B7', '#A7F3D0'],
    success: '#059669',
    warning: '#D97706',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'HelveticaNeue-Bold', bodyFont: 'HelveticaNeue' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A friendly green theme that encourages interaction and participation, ideal for hands-on workshops, team training sessions, how-to guides, and collaborative learning.',
    tags: ['workshop', 'training', 'green', 'interactive'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 160, stops: [{ color: '#ECFDF5', offset: 0 }, { color: '#A7F3D040', offset: 100 }] } },
          { type: 'shape', frame: { x: 80, y: 100, width: 864, height: 548 }, shapeType: 'roundedRectangle', color: '#FFFFFF70' },
          { type: 'shape', frame: { x: -80, y: 500, width: 300, height: 300 }, shapeType: 'ellipse', color: '#05966910' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#059669' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', color: '#059669' },
          { type: 'shape', frame: { x: 40, y: 30, width: 944, height: 688 }, shapeType: 'roundedRectangle', color: '#A7F3D010' },
        ],
      },
    ],
  }
);

// ============================================================
// Dark/Modern Trends
// ============================================================

export const THEME_MIDNIGHT: StyleTheme = createStyleTheme(
  'midnight',
  'Midnight',
  {
    primary: '#38BDF8',
    secondary: '#7DD3FC',
    background: '#020617',
    textPrimary: '#F0F9FF',
    textSecondary: '#7DD3FC',
    accent: ['#38BDF8', '#7DD3FC', '#BAE6FD', '#E0F2FE'],
    success: '#34D399',
    warning: '#FBBF24',
    error: '#FB7185',
  },
  {
    typography: { titleFont: 'HelveticaNeue-Bold', bodyFont: 'HelveticaNeue-Light' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'A deep midnight blue theme with sky blue accents that feels sleek and modern, suited for late-night product reveals, space and astronomy topics, and polished tech demos.',
    tags: ['midnight', 'dark', 'sky', 'modern'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#020617' },
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'radial', stops: [{ color: '#38BDF815', offset: 0 }, { color: '#02061700', offset: 70 }] } },
          { type: 'shape', frame: { x: 80, y: 150, width: 864, height: 450 }, shapeType: 'roundedRectangle', color: '#0F172A80' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#020617' },
          { type: 'shape', frame: { x: 0, y: 740, width: 1024, height: 8 }, shapeType: 'rectangle', color: '#38BDF8' },
        ],
      },
    ],
  }
);

export const THEME_NEON: StyleTheme = createStyleTheme(
  'neon',
  'Neon',
  {
    primary: '#D946EF',
    secondary: '#E879F9',
    background: '#09090B',
    textPrimary: '#FAFAFA',
    textSecondary: '#D4D4D8',
    accent: ['#D946EF', '#E879F9', '#F0ABFC', '#F5D0FE'],
    success: '#22C55E',
    warning: '#EAB308',
    error: '#EF4444',
  },
  {
    typography: { titleFont: 'Futura-Medium', bodyFont: 'HelveticaNeue' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'An electrifying neon fuchsia on black theme for nightlife, music, entertainment, gaming events, and any content that needs high-energy visual punch.',
    tags: ['neon', 'dark', 'fuchsia', 'electric'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#09090B' },
          { type: 'shape', frame: { x: -100, y: 500, width: 400, height: 400 }, shapeType: 'ellipse', color: '#D946EF15' },
          { type: 'shape', frame: { x: 750, y: -50, width: 350, height: 350 }, shapeType: 'ellipse', color: '#D946EF10' },
          { type: 'shape', frame: { x: 0, y: 730, width: 1024, height: 18 }, shapeType: 'rectangle', color: '#D946EF' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#09090B' },
          { type: 'shape', frame: { x: 1019, y: 0, width: 5, height: 748 }, shapeType: 'rectangle', color: '#D946EF' },
        ],
      },
    ],
  }
);

export const THEME_PASTEL: StyleTheme = createStyleTheme(
  'pastel',
  'Pastel',
  {
    primary: '#C084FC',
    secondary: '#D8B4FE',
    background: '#FDF4FF',
    textPrimary: '#3B0764',
    textSecondary: '#6B21A8',
    accent: ['#C084FC', '#D8B4FE', '#E9D5FF', '#F3E8FF'],
    success: '#4ADE80',
    warning: '#FACC15',
    error: '#FB7185',
  },
  {
    typography: { titleFont: 'Lora-Regular', bodyFont: 'Lato-Regular', lineHeight: 1.6 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'A gentle pastel theme with soft lavender and pink tones, ideal for beauty brands, self-care content, wedding planning, baby showers, and lifestyle blogs.',
    tags: ['pastel', 'soft', 'purple', 'gentle'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#FDF4FF', offset: 0 }, { color: '#F3E8FF', offset: 50 }, { color: '#E9D5FF30', offset: 100 }] } },
          { type: 'gradient-shape', frame: { x: 600, y: -80, width: 500, height: 500 }, shapeType: 'ellipse', gradient: { type: 'radial', stops: [{ color: '#C084FC18', offset: 0 }, { color: '#FDF4FF00', offset: 70 }] } },
          { type: 'shape', frame: { x: 80, y: 100, width: 864, height: 548 }, shapeType: 'roundedRectangle', color: '#FFFFFF60' },
          { type: 'shape', frame: { x: 0, y: 730, width: 1024, height: 18 }, shapeType: 'rectangle', color: '#C084FC' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#C084FC' },
          { type: 'shape', frame: { x: 60, y: 40, width: 904, height: 668 }, shapeType: 'roundedRectangle', color: '#C084FC08' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['image-top', 'image-right'],
    },
  }
);

// ============================================================
// New Themes — filling style gaps
// ============================================================

export const THEME_SUNSET: StyleTheme = createStyleTheme(
  'sunset',
  'Sunset',
  {
    primary: '#F97316',
    secondary: '#EC4899',
    background: '#1C1017',
    textPrimary: '#FFF7ED',
    textSecondary: '#FDBA74',
    accent: ['#F97316', '#EC4899', '#A855F7', '#F43F5E'],
    success: '#22C55E',
    warning: '#FBBF24',
    error: '#EF4444',
  },
  {
    typography: { titleFont: 'Montserrat-Bold', bodyFont: 'HelveticaNeue-Light' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'A vibrant sunset gradient theme blending orange, pink, and purple, perfect for inspirational talks, travel content, motivational presentations, and creative storytelling.',
    tags: ['sunset', 'warm', 'gradient', 'colorful'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#F97316', offset: 0 }, { color: '#EC4899', offset: 50 }, { color: '#A855F7', offset: 100 }] } },
          { type: 'shape', frame: { x: 80, y: 140, width: 864, height: 470 }, shapeType: 'roundedRectangle', color: '#00000040' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#1C1017' },
          { type: 'gradient-shape', frame: { x: 0, y: 740, width: 1024, height: 8 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 90, stops: [{ color: '#F97316', offset: 0 }, { color: '#EC4899', offset: 50 }, { color: '#A855F7', offset: 100 }] } },
        ],
      },
    ],
  }
);

export const THEME_MONOCHROME: StyleTheme = createStyleTheme(
  'monochrome',
  'Monochrome',
  {
    primary: '#FFFFFF',
    secondary: '#E5E5E5',
    background: '#171717',
    textPrimary: '#FAFAFA',
    textSecondary: '#A3A3A3',
    accent: ['#FFFFFF', '#D4D4D4', '#A3A3A3', '#737373'],
    success: '#FFFFFF',
    warning: '#D4D4D4',
    error: '#A3A3A3',
  },
  {
    typography: { titleFont: 'HelveticaNeue-Bold', bodyFont: 'HelveticaNeue-Light', lineHeight: 1.6 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'A sophisticated monochrome theme with strong typography that lets the words do the talking, suited for thought leadership, editorial content, and focused messaging.',
    tags: ['monochrome', 'gray', 'dark', 'typography'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0A0A0A' },
          { type: 'gradient-shape', frame: { x: 250, y: 200, width: 524, height: 400 }, shapeType: 'ellipse', gradient: { type: 'radial', stops: [{ color: '#FFFFFF0C', offset: 0 }, { color: '#0A0A0A00', offset: 70 }] } },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#FFFFFF20' },
          { type: 'shape', frame: { x: 0, y: 670, width: 1024, height: 10 }, shapeType: 'rectangle', color: '#FFFFFF' },
          { type: 'shape', frame: { x: 80, y: 680, width: 864, height: 1 }, shapeType: 'rectangle', color: '#FFFFFF' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#171717' },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 2 }, shapeType: 'rectangle', color: '#FFFFFF20' },
          { type: 'shape', frame: { x: 80, y: 60, width: 864, height: 1 }, shapeType: 'rectangle', color: '#FFFFFF30' },
        ],
      },
    ],
  }
);

export const THEME_RETRO: StyleTheme = createStyleTheme(
  'retro',
  'Retro',
  {
    primary: '#92400E',
    secondary: '#B45309',
    background: '#FFFBEB',
    textPrimary: '#451A03',
    textSecondary: '#78350F',
    accent: ['#92400E', '#B45309', '#D97706', '#78350F'],
    success: '#166534',
    warning: '#CA8A04',
    error: '#991B1B',
  },
  {
    typography: { titleFont: 'Georgia-Bold', bodyFont: 'Georgia', lineHeight: 1.6 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A nostalgic vintage theme with warm earth tones and classic serif fonts, great for history topics, heritage brands, craft businesses, and storytelling with a retro feel.',
    tags: ['retro', 'vintage', 'earth', 'warm'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#FFFBEB' },
          { type: 'shape', frame: { x: 40, y: 40, width: 944, height: 668 }, shapeType: 'rectangle', color: '#FEF3C710' },
          // Double-line frame (outer)
          { type: 'shape', frame: { x: 40, y: 40, width: 944, height: 4 }, shapeType: 'rectangle', color: '#92400E' },
          { type: 'shape', frame: { x: 40, y: 704, width: 944, height: 4 }, shapeType: 'rectangle', color: '#92400E' },
          { type: 'shape', frame: { x: 40, y: 40, width: 4, height: 668 }, shapeType: 'rectangle', color: '#92400E' },
          { type: 'shape', frame: { x: 980, y: 40, width: 4, height: 668 }, shapeType: 'rectangle', color: '#92400E' },
          // Double-line frame (inner)
          { type: 'shape', frame: { x: 48, y: 48, width: 928, height: 1 }, shapeType: 'rectangle', color: '#92400E40' },
          { type: 'shape', frame: { x: 48, y: 699, width: 928, height: 1 }, shapeType: 'rectangle', color: '#92400E40' },
          { type: 'shape', frame: { x: 48, y: 48, width: 1, height: 652 }, shapeType: 'rectangle', color: '#92400E40' },
          { type: 'shape', frame: { x: 975, y: 48, width: 1, height: 652 }, shapeType: 'rectangle', color: '#92400E40' },
          // Corner accent rectangles
          { type: 'shape', frame: { x: 36, y: 36, width: 12, height: 12 }, shapeType: 'rectangle', color: '#92400E' },
          { type: 'shape', frame: { x: 976, y: 36, width: 12, height: 12 }, shapeType: 'rectangle', color: '#92400E' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#92400E' },
          { type: 'shape', frame: { x: 0, y: 5, width: 1024, height: 1 }, shapeType: 'rectangle', color: '#92400E30' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['image-left', 'image-right'],
    },
  }
);

export const THEME_FRESH: StyleTheme = createStyleTheme(
  'fresh',
  'Fresh',
  {
    primary: '#16A34A',
    secondary: '#22D3EE',
    background: '#F0FDF4',
    textPrimary: '#14532D',
    textSecondary: '#166534',
    accent: ['#16A34A', '#22D3EE', '#84CC16', '#A3E635'],
    success: '#16A34A',
    warning: '#EAB308',
    error: '#DC2626',
  },
  {
    typography: { titleFont: 'Montserrat-Bold', bodyFont: 'Lato-Regular' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'light',
    description: 'A fresh lime and mint green theme that feels clean and revitalizing, perfect for wellness brands, healthy living, fitness programs, nutrition plans, and lifestyle content.',
    tags: ['fresh', 'green', 'mint', 'wellness', 'lifestyle'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#F0FDF4', offset: 0 }, { color: '#DCFCE7', offset: 100 }] } },
          { type: 'shape', frame: { x: 0, y: 720, width: 512, height: 28 }, shapeType: 'rectangle', color: '#16A34A' },
          { type: 'shape', frame: { x: 512, y: 720, width: 512, height: 28 }, shapeType: 'rectangle', color: '#22D3EE' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 4 }, shapeType: 'rectangle', color: '#16A34A' },
        ],
      },
    ],
  }
);

export const THEME_LUXE: StyleTheme = createStyleTheme(
  'luxe',
  'Luxe',
  {
    primary: '#D4AF37',
    secondary: '#B8860B',
    background: '#0C0A09',
    textPrimary: '#FAFAF9',
    textSecondary: '#A8A29E',
    accent: ['#D4AF37', '#B8860B', '#F5DEB3', '#8B7355'],
    success: '#22C55E',
    warning: '#D4AF37',
    error: '#EF4444',
  },
  {
    typography: { titleFont: 'Georgia-Bold', bodyFont: 'Lato-Light', lineHeight: 1.6 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'professional',
    description: 'An opulent dark theme with gold accents that exudes luxury and exclusivity, ideal for premium brands, high-end products, fashion, jewelry, and VIP event presentations.',
    tags: ['luxe', 'luxury', 'gold', 'premium', 'elegant'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0C0A09' },
          { type: 'gradient-shape', frame: { x: 300, y: 200, width: 500, height: 500 }, shapeType: 'ellipse', gradient: { type: 'radial', stops: [{ color: '#D4AF3715', offset: 0 }, { color: '#0C0A0900', offset: 70 }] } },
          { type: 'shape', frame: { x: 60, y: 60, width: 904, height: 628 }, shapeType: 'roundedRectangle', color: '#1C191740' },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 2 }, shapeType: 'rectangle', color: '#D4AF3740' },
          { type: 'shape', frame: { x: 1020, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', color: '#D4AF3730' },
          { type: 'shape', frame: { x: 0, y: 720, width: 1024, height: 28 }, shapeType: 'rectangle', color: '#D4AF37' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0C0A09' },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#D4AF37' },
          { type: 'shape', frame: { x: 1020, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', color: '#D4AF3720' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['full-image', 'image-text-overlay', 'full-image-content', 'image-bg-card'],
      screenTypeLayouts: { 'image-focus': 'full-image' },
    },
  }
);

export const THEME_PLAYFUL: StyleTheme = createStyleTheme(
  'playful',
  'Playful',
  {
    primary: '#7C3AED',
    secondary: '#EC4899',
    background: '#FFFFFF',
    textPrimary: '#1E1B4B',
    textSecondary: '#4C1D95',
    accent: ['#7C3AED', '#EC4899', '#F59E0B', '#06B6D4'],
    success: '#22C55E',
    warning: '#F59E0B',
    error: '#EF4444',
  },
  {
    typography: { titleFont: 'Montserrat-Bold', bodyFont: 'OpenSans' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'A bright, playful multicolor theme designed for kids education, elementary school lessons, daycare activities, children\'s events, and fun learning content.',
    tags: ['playful', 'kids', 'education', 'bright', 'fun'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 720, width: 256, height: 28 }, shapeType: 'rectangle', color: '#7C3AED' },
          { type: 'shape', frame: { x: 256, y: 720, width: 256, height: 28 }, shapeType: 'rectangle', color: '#EC4899' },
          { type: 'shape', frame: { x: 512, y: 720, width: 256, height: 28 }, shapeType: 'rectangle', color: '#F59E0B' },
          { type: 'shape', frame: { x: 768, y: 720, width: 256, height: 28 }, shapeType: 'rectangle', color: '#06B6D4' },
          { type: 'shape', frame: { x: 800, y: -40, width: 200, height: 200 }, shapeType: 'ellipse', color: '#EC489915' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', color: '#7C3AED' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['image-grid', 'two-images', 'image-top'],
    },
  }
);

export const THEME_BLUEPRINT: StyleTheme = createStyleTheme(
  'blueprint',
  'Blueprint',
  {
    primary: '#60A5FA',
    secondary: '#3B82F6',
    background: '#0F1729',
    textPrimary: '#E2E8F0',
    textSecondary: '#94A3B8',
    accent: ['#60A5FA', '#3B82F6', '#93C5FD', '#BFDBFE'],
    success: '#34D399',
    warning: '#FBBF24',
    error: '#F87171',
  },
  {
    typography: { titleFont: 'Roboto-Bold', bodyFont: 'Roboto-Light' },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'A dark blue blueprint-style theme with grid accents, built for engineering diagrams, technical architecture, software documentation, and infrastructure planning.',
    tags: ['blueprint', 'engineering', 'dark', 'technical', 'grid'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0F1729' },
          { type: 'shape', frame: { x: 60, y: 60, width: 1, height: 628 }, shapeType: 'rectangle', color: '#60A5FA15' },
          { type: 'shape', frame: { x: 963, y: 60, width: 1, height: 628 }, shapeType: 'rectangle', color: '#60A5FA15' },
          { type: 'shape', frame: { x: 60, y: 60, width: 904, height: 1 }, shapeType: 'rectangle', color: '#60A5FA15' },
          { type: 'shape', frame: { x: 60, y: 688, width: 904, height: 1 }, shapeType: 'rectangle', color: '#60A5FA15' },
          { type: 'shape', frame: { x: 0, y: 740, width: 1024, height: 8 }, shapeType: 'rectangle', color: '#60A5FA' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0F1729' },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#60A5FA' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['three-column', 'two-column'],
      screenTypeLayouts: { 'comparison': 'three-column' },
    },
  }
);

/**
 * SlideDoc Theme
 * Migrated from FlowVella template n9f (themeId 59)
 * Document-style presentations with cyan header, gold accents, grid layout
 */
export const THEME_SLIDEDOC: StyleTheme = createStyleTheme(
  'slidedoc',
  'SlideDoc',
  {
    primary: '#35D4EC',
    secondary: '#F6B10B',
    background: '#F3F3F3',
    textPrimary: '#2E2E2F',
    textSecondary: '#878470',
    accent: ['#35D4EC', '#F6B10B', '#D9340B'],
  },
  {
    typography: {
      titleFont: 'HelveticaNeue-CondensedBold',
      bodyFont: 'HelveticaNeue',
      baseFontSize: 28,
      lineHeight: 1.5,
    },
    flowvellaThemeId: '59',
    flowvellaThemeStringId: 'n9f',
    category: 'professional',
    description: 'Document-style presentations for reports, white papers, and detailed content. Grid-based layout with cyan header, gold accents, and clean typography.',
    tags: ['slidedoc', 'document', 'report', 'professional', 'grid'],
    blockStyles: {
      title: {
        fontName: 'HelveticaNeue-CondensedBold',
        fontSize: 48,
        textColor: '#FFFFFF',
      },
      subtitle: {
        fontName: 'Helvetica-Oblique',
        fontSize: 22,
        textColor: '#2E2E2F',
      },
      body: {
        fontSize: 16,
        textColor: '#2E2E2F',
      },
      callout: {
        backgroundColor: '#F6B10B',
        textColor: '#2E2E2F',
      },
    },
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          // Cyan background for title slide
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#35D4EC' },
          // Bottom divider line
          { type: 'shape', frame: { x: 40, y: 700, width: 944, height: 2 }, shapeType: 'rectangle', color: '#444444' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          // Light gray background
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#F3F3F3' },
          // Top accent line
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 3 }, shapeType: 'rectangle', color: '#35D4EC' },
          // Content divider line
          { type: 'shape', frame: { x: 40, y: 38, width: 944, height: 1 }, shapeType: 'rectangle', color: '#888888' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['three-column', 'two-column', 'title-body'],
      screenTypeLayouts: { 'content': 'three-column', 'comparison': 'three-column' },
    },
  }
);

export const THEME_SLATE: StyleTheme = createStyleTheme(
  'slate',
  'Slate',
  {
    primary: '#64748B',
    secondary: '#94A3B8',
    background: '#1E293B',
    textPrimary: '#F1F5F9',
    textSecondary: '#94A3B8',
    accent: ['#64748B', '#94A3B8', '#CBD5E1', '#475569'],
    success: '#34D399',
    warning: '#FBBF24',
    error: '#F87171',
  },
  {
    typography: { titleFont: 'HelveticaNeue-Bold', bodyFont: 'HelveticaNeue', lineHeight: 1.5 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'A neutral dark slate theme with muted blue-gray tones, versatile enough for any professional dark-mode presentation from internal reports to product updates.',
    tags: ['slate', 'neutral', 'dark', 'muted', 'gray'],
    decorations: [
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0F172A' },
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'radial', stops: [{ color: '#64748B12', offset: 0 }, { color: '#0F172A00', offset: 70 }] } },
          { type: 'shape', frame: { x: 0, y: 730, width: 1024, height: 18 }, shapeType: 'rectangle', color: '#64748B' },
        ],
      },
      {
        appliesTo: ['content', 'two-column', 'comparison', 'timeline', 'team'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#1E293B' },
          { type: 'shape', frame: { x: 1020, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', color: '#64748B' },
        ],
      },
    ],
  }
);

// ============================================================
// New Themes (2025)
// ============================================================

export const THEME_CITRUS_POP: StyleTheme = createStyleTheme(
  'citrus-pop',
  'Citrus Pop',
  {
    primary: '#FF5500',
    secondary: '#CCFF00',
    background: '#FFFFFF',
    textPrimary: '#0A0A0A',
    textSecondary: '#555555',
    accent: ['#FF5500', '#CCFF00', '#FF3366'],
    success: '#00CC66',
    warning: '#FFAA00',
    error: '#FF3366',
  },
  {
    typography: { titleFont: 'Futura-Bold', bodyFont: 'HelveticaNeue', baseFontSize: 28, lineHeight: 1.55 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'Unapologetically loud. Acid lime title slides, bright orange-to-white gradients, neon accents. For brands that want to be noticed.',
    tags: ['colorful', 'bright', 'bold', 'GenZ', 'playful', 'energetic', 'marketing'],
    blockStyles: {
      title: { fontSize: 63, textColor: '#0A0A0A', textAlignment: 'left', letterSpacing: -3, lineHeight: 1.0, textTransform: 'uppercase' },
      subtitle: { fontSize: 22, textColor: '#FF5500', fontWeight: 'bold', textAlignment: 'left', textTransform: 'uppercase', letterSpacing: 3 },
      body: { fontSize: 20, lineHeight: 1.7 },
      quote: { fontSize: 39, fontName: 'Futura-Bold', textColor: '#CCFF00', textAlignment: 'center', lineHeight: 1.15, textTransform: 'uppercase' },
      callout: { fontSize: 17, backgroundColor: '#FF550018', textColor: '#0A0A0A', border: { color: '#FF5500', width: 3, radius: 0 }, padding: { top: 17, right: 20, bottom: 17, left: 20 } },
    },
    mediaStyles: {
      image: { cornerRadius: 0, border: { color: '#0A0A0A', width: 4, radius: 0 } },
      video: { cornerRadius: 0, border: { color: '#FF5500', width: 3, radius: 0 } },
    },
    decorations: [
      {
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 180, stops: [{ color: '#FFFFFF', offset: 0 }, { color: '#FFF5EB', offset: 60 }, { color: '#FFE8D0', offset: 100 }] } },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 10 }, shapeType: 'rectangle', color: '#FF5500' },
          { type: 'shape', frame: { x: 800, y: -104, width: 267, height: 347 }, shapeType: 'ellipse', color: '#CCFF0035' },
          { type: 'shape', frame: { x: -53, y: 485, width: 213, height: 277 }, shapeType: 'ellipse', color: '#FF550020' },
        ],
      },
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 145, stops: [{ color: '#CCFF00', offset: 0 }, { color: '#B8E600', offset: 40 }, { color: '#E0FF40', offset: 100 }] } },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 10 }, shapeType: 'rectangle', color: '#FF5500' },
          { type: 'shape', frame: { x: 0, y: 738, width: 1024, height: 10 }, shapeType: 'rectangle', color: '#FF5500' },
          { type: 'shape', frame: { x: 720, y: -139, width: 373, height: 486 }, shapeType: 'ellipse', color: '#FFFFFF30' },
        ],
      },
      {
        appliesTo: ['quote'],
        elements: [
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', color: '#0A0A0A' },
          { type: 'shape', frame: { x: 0, y: 0, width: 1024, height: 10 }, shapeType: 'rectangle', color: '#CCFF00' },
          { type: 'shape', frame: { x: 0, y: 738, width: 1024, height: 10 }, shapeType: 'rectangle', color: '#FF5500' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['three-column', 'image-grid', 'two-images', 'image-left-wide', 'image-right-wide'],
    },
  }
);

export const THEME_CORAL_SUNSET: StyleTheme = createStyleTheme(
  'coral-sunset',
  'Coral Sunset',
  {
    primary: '#FF6B6B',
    secondary: '#FFA234',
    background: '#D4455A',
    textPrimary: '#FFFFFF',
    textSecondary: '#FFE0D0',
    accent: ['#FFFFFF', '#FFE66D', '#FF4081'],
    success: '#4ECDC4',
    warning: '#FFE66D',
    error: '#FF2040',
  },
  {
    typography: { titleFont: 'Avenir-Heavy', bodyFont: 'Avenir-Book', baseFontSize: 28, lineHeight: 1.65 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'colorful',
    description: 'Rich diagonal sweep from deep coral through peach into warm gold. White text on bold warm gradients. For storytelling and brand narratives.',
    tags: ['colorful', 'warm', 'gradient', 'coral', 'sunset', 'bold', 'storytelling'],
    blockStyles: {
      title: { fontSize: 60, textColor: '#FFFFFF', textAlignment: 'left', letterSpacing: -2, lineHeight: 1.05 },
      subtitle: { fontSize: 23, textColor: '#FFE0D0', fontWeight: 'light', textAlignment: 'left' },
      body: { fontSize: 20, lineHeight: 1.7, textColor: '#FFFFFF' },
      quote: { fontSize: 36, fontName: 'Georgia-Italic', textColor: '#FFFFFF', textAlignment: 'center', lineHeight: 1.35 },
      callout: { fontSize: 17, backgroundColor: '#FFFFFF20', textColor: '#FFFFFF', border: { color: '#FFFFFF40', width: 1, radius: 14 }, padding: { top: 15, right: 20, bottom: 15, left: 20 } },
    },
    mediaStyles: {
      image: { cornerRadius: 13, border: { color: '#FFFFFF30', width: 3, radius: 13 }, shadow: { color: '#00000025', blur: 25, offsetX: 0, offsetY: 10 } },
      video: { cornerRadius: 10, border: { color: '#FFFFFF25', width: 2, radius: 10 } },
    },
    decorations: [
      {
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#E8456B', offset: 0 }, { color: '#D4455A', offset: 30 }, { color: '#E07040', offset: 65 }, { color: '#D4943A', offset: 100 }] } },
          { type: 'shape', frame: { x: -107, y: -139, width: 373, height: 485 }, shapeType: 'ellipse', color: '#FFFFFF12' },
          { type: 'shape', frame: { x: 720, y: 381, width: 347, height: 450 }, shapeType: 'ellipse', color: '#FFE66D15' },
        ],
      },
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 150, stops: [{ color: '#C03050', offset: 0 }, { color: '#D4455A', offset: 25 }, { color: '#E86040', offset: 55 }, { color: '#E8A030', offset: 85 }, { color: '#FFD060', offset: 100 }] } },
          { type: 'shape', frame: { x: 693, y: -208, width: 480, height: 624 }, shapeType: 'ellipse', color: '#FFFFFF15' },
          { type: 'shape', frame: { x: -133, y: 346, width: 373, height: 485 }, shapeType: 'ellipse', color: '#FF406020' },
        ],
      },
      {
        appliesTo: ['quote'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'radial', stops: [{ color: '#E86040', offset: 0 }, { color: '#C03050', offset: 100 }] } },
          { type: 'shape', frame: { x: 245, y: 97, width: 533, height: 555 }, shapeType: 'ellipse', color: '#FFE66D12' },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['three-column', 'image-grid', 'two-images', 'image-left-wide', 'image-right-wide'],
    },
  }
);

export const THEME_COSMIC_DRIFT: StyleTheme = createStyleTheme(
  'cosmic-drift',
  'Cosmic Drift',
  {
    primary: '#8338EC',
    secondary: '#3A86FF',
    background: '#140030',
    textPrimary: '#FFFFFF',
    textSecondary: '#C0B0E0',
    accent: ['#8338EC', '#3A86FF', '#FF006E'],
    success: '#06D6A0',
    warning: '#FFD166',
    error: '#FF006E',
  },
  {
    typography: { titleFont: 'SFProDisplay-Bold', bodyFont: 'Avenir-Book', baseFontSize: 28, lineHeight: 1.6 },
    flowvellaThemeId: '10', flowvellaThemeStringId: 'dyl',
    category: 'dark',
    description: 'Deep space nebula with purple, blue, and hot pink gradients. Dramatic backgrounds shift between slide types.',
    tags: ['dark', 'space', 'cosmic', 'vivid', 'gradient', 'futuristic', 'AI'],
    blockStyles: {
      title: { fontSize: 58, textColor: '#FFFFFF', textAlignment: 'left', letterSpacing: -1.5, lineHeight: 1.08 },
      subtitle: { fontSize: 24, textColor: '#C0B0E0', fontWeight: 'light', textAlignment: 'left' },
      body: { fontSize: 20, lineHeight: 1.7, textColor: '#E8E0F8' },
      quote: { fontSize: 34, fontName: 'Georgia-Italic', textColor: '#3A86FF', textAlignment: 'center', lineHeight: 1.35 },
      callout: { fontSize: 17, backgroundGradient: { type: 'linear', angle: 135, stops: [{ color: '#8338EC30', offset: 0 }, { color: '#3A86FF20', offset: 100 }] }, textColor: '#FFFFFF', border: { color: '#8338EC50', width: 1, radius: 14 }, padding: { top: 15, right: 20, bottom: 15, left: 20 } },
    },
    mediaStyles: {
      image: { cornerRadius: 14, border: { color: '#8338EC35', width: 2, radius: 14 }, shadow: { color: '#8338EC25', blur: 28, offsetX: 0, offsetY: 11 } },
      video: { cornerRadius: 11, border: { color: '#3A86FF40', width: 2, radius: 11 } },
    },
    decorations: [
      {
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 135, stops: [{ color: '#1A0050', offset: 0 }, { color: '#200060', offset: 30 }, { color: '#0A1060', offset: 60 }, { color: '#080840', offset: 100 }] } },
          { type: 'shape', frame: { x: -107, y: -69, width: 427, height: 555 }, shapeType: 'ellipse', color: '#8338EC28' },
          { type: 'shape', frame: { x: 640, y: 243, width: 480, height: 624 }, shapeType: 'ellipse', color: '#3A86FF20' },
          { type: 'shape', frame: { x: 320, y: -208, width: 320, height: 416 }, shapeType: 'ellipse', color: '#FF006E18' },
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 0, stops: [{ color: '#8338EC', offset: 0 }, { color: '#3A86FF', offset: 50 }, { color: '#FF006E', offset: 100 }] } },
        ],
      },
      {
        appliesTo: ['title-slide'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'radial', stops: [{ color: '#2A0080', offset: 0 }, { color: '#180050', offset: 50 }, { color: '#0A0030', offset: 100 }] } },
          { type: 'shape', frame: { x: -160, y: -208, width: 587, height: 763 }, shapeType: 'ellipse', color: '#8338EC30' },
          { type: 'shape', frame: { x: 533, y: 138, width: 587, height: 763 }, shapeType: 'ellipse', color: '#3A86FF25' },
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 0, stops: [{ color: '#8338EC', offset: 0 }, { color: '#3A86FF', offset: 50 }, { color: '#FF006E', offset: 100 }] } },
        ],
      },
      {
        appliesTo: ['quote'],
        elements: [
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 1024, height: 748 }, shapeType: 'rectangle', gradient: { type: 'radial', stops: [{ color: '#2A1070', offset: 0 }, { color: '#140030', offset: 100 }] } },
          { type: 'shape', frame: { x: 192, y: 28, width: 640, height: 693 }, shapeType: 'ellipse', color: '#3A86FF18' },
          { type: 'gradient-shape', frame: { x: 0, y: 0, width: 4, height: 748 }, shapeType: 'rectangle', gradient: { type: 'linear', angle: 0, stops: [{ color: '#8338EC', offset: 0 }, { color: '#3A86FF', offset: 50 }, { color: '#FF006E', offset: 100 }] } },
        ],
      },
    ],
    layoutPreferences: {
      preferredLayoutIds: ['three-column', 'image-grid', 'two-images', 'image-left-wide', 'image-right-wide'],
    },
  }
);

/**
 * All predefined themes in a map for easy lookup
 */
export const PREDEFINED_THEMES: Map<string, StyleTheme> = new Map([
  // Default theme — ultra-clean, fast
  [THEME_CLEAN.id, THEME_CLEAN],
  // Featured — themes with visual decorations first
  [THEME_CONFERENCE.id, THEME_CONFERENCE],
  [THEME_STARTUP.id, THEME_STARTUP],
  [THEME_CORPORATE_BLUE.id, THEME_CORPORATE_BLUE],
  [THEME_NATURE.id, THEME_NATURE],
  [THEME_MIDNIGHT.id, THEME_MIDNIGHT],
  [THEME_GRADIENT_BLUE.id, THEME_GRADIENT_BLUE],
  // More decorated themes
  [THEME_MODERN_DARK.id, THEME_MODERN_DARK],
  [THEME_TECH.id, THEME_TECH],
  [THEME_NEON.id, THEME_NEON],
  [THEME_PORTFOLIO.id, THEME_PORTFOLIO],
  [THEME_FINANCE.id, THEME_FINANCE],
  [THEME_WARM.id, THEME_WARM],
  [THEME_GALLERY.id, THEME_GALLERY],
  // Classic themes
  [THEME_MODERN_LIGHT.id, THEME_MODERN_LIGHT],
  [THEME_PROFESSIONAL.id, THEME_PROFESSIONAL],
  [THEME_CREATIVE.id, THEME_CREATIVE],
  [THEME_MINIMAL.id, THEME_MINIMAL],
  [THEME_OCEAN.id, THEME_OCEAN],
  // Professional Variations
  [THEME_EXECUTIVE.id, THEME_EXECUTIVE],
  [THEME_CONSULTING.id, THEME_CONSULTING],
  // Education
  [THEME_CLASSROOM.id, THEME_CLASSROOM],
  [THEME_ACADEMIC.id, THEME_ACADEMIC],
  [THEME_SCIENCE.id, THEME_SCIENCE],
  // Industry
  [THEME_HEALTHCARE.id, THEME_HEALTHCARE],
  [THEME_REAL_ESTATE.id, THEME_REAL_ESTATE],
  [THEME_BOLD_TYPE.id, THEME_BOLD_TYPE],
  // Events
  [THEME_WORKSHOP.id, THEME_WORKSHOP],
  [THEME_PASTEL.id, THEME_PASTEL],
  // New themes
  [THEME_SUNSET.id, THEME_SUNSET],
  [THEME_MONOCHROME.id, THEME_MONOCHROME],
  [THEME_RETRO.id, THEME_RETRO],
  [THEME_FRESH.id, THEME_FRESH],
  [THEME_LUXE.id, THEME_LUXE],
  [THEME_PLAYFUL.id, THEME_PLAYFUL],
  [THEME_BLUEPRINT.id, THEME_BLUEPRINT],
  [THEME_SLATE.id, THEME_SLATE],
  // SlideDoc — migrated from FlowVella template
  [THEME_SLIDEDOC.id, THEME_SLIDEDOC],
  // New 2025 themes
  [THEME_CITRUS_POP.id, THEME_CITRUS_POP],
  [THEME_CORAL_SUNSET.id, THEME_CORAL_SUNSET],
  [THEME_COSMIC_DRIFT.id, THEME_COSMIC_DRIFT],
]);

/**
 * All predefined themes as an array
 */
export const ALL_THEMES: StyleTheme[] = Array.from(PREDEFINED_THEMES.values());

/**
 * Featured themes — curated set of 10 essential themes
 * Shown by default in the creator sidebar and editor theme picker
 */
export const FEATURED_THEMES: StyleTheme[] = [
  THEME_CLEAN,           // Default, universal
  THEME_MODERN_DARK,     // Tech, dark mode
  THEME_MODERN_LIGHT,    // Clean light alternative
  THEME_PROFESSIONAL,    // Business
  THEME_STARTUP,         // Pitch decks
  THEME_NATURE,          // Organic, wellness
  THEME_BOLD_TYPE,       // Creative, typography
  THEME_MINIMAL,         // Ultra-simple
  THEME_CORAL_SUNSET,    // Warm, elegant
  THEME_COSMIC_DRIFT,    // Modern, techy
];

/**
 * Get themes by category
 */
export function getThemesByCategory(category: StyleTheme['category']): StyleTheme[] {
  return ALL_THEMES.filter((theme) => theme.category === category);
}

/**
 * Get themes by tags
 */
export function getThemesByTags(tags: string[]): StyleTheme[] {
  return ALL_THEMES.filter((theme) =>
    tags.some((tag) => theme.tags?.includes(tag))
  );
}

/**
 * Get a theme by ID
 */
export function getThemeById(id: string): StyleTheme | undefined {
  return PREDEFINED_THEMES.get(id);
}

/**
 * Suggest themes based on presentation metadata
 */
export function suggestThemes(
  tone?: string,
  industry?: string,
  purpose?: string
): StyleTheme[] {
  const suggestions: StyleTheme[] = [];

  // Match by tone
  if (tone === 'professional' || tone === 'formal') {
    suggestions.push(THEME_CORPORATE_BLUE, THEME_EXECUTIVE, THEME_CONSULTING);
  } else if (tone === 'creative') {
    suggestions.push(THEME_CREATIVE, THEME_BOLD_TYPE, THEME_PORTFOLIO);
  } else if (tone === 'casual') {
    suggestions.push(THEME_WARM, THEME_PASTEL, THEME_CLASSROOM);
  } else if (tone === 'educational') {
    suggestions.push(THEME_CLASSROOM, THEME_SCIENCE, THEME_ACADEMIC);
  }

  // Match by industry
  const ind = industry?.toLowerCase() || '';
  if (ind.includes('tech') || ind.includes('software')) {
    suggestions.push(THEME_TECH, THEME_MIDNIGHT, THEME_STARTUP);
  } else if (ind.includes('finance') || ind.includes('banking')) {
    suggestions.push(THEME_FINANCE, THEME_CONSULTING, THEME_EXECUTIVE);
  } else if (ind.includes('health') || ind.includes('wellness') || ind.includes('medical')) {
    suggestions.push(THEME_HEALTHCARE, THEME_NATURE, THEME_WORKSHOP);
  } else if (ind.includes('education') || ind.includes('school') || ind.includes('university')) {
    suggestions.push(THEME_CLASSROOM, THEME_ACADEMIC, THEME_SCIENCE);
  } else if (ind.includes('real estate') || ind.includes('property')) {
    suggestions.push(THEME_REAL_ESTATE, THEME_EXECUTIVE, THEME_GALLERY);
  } else if (ind.includes('design') || ind.includes('creative') || ind.includes('art')) {
    suggestions.push(THEME_PORTFOLIO, THEME_GALLERY, THEME_NEON);
  } else if (ind.includes('startup') || ind.includes('venture')) {
    suggestions.push(THEME_STARTUP, THEME_MODERN_DARK, THEME_NEON);
  } else if (ind.includes('marketing') || ind.includes('advertising')) {
    suggestions.push(THEME_BOLD_TYPE, THEME_CREATIVE, THEME_CONFERENCE);
  }

  // Match by purpose
  if (purpose === 'pitch') {
    suggestions.push(THEME_STARTUP, THEME_MODERN_DARK, THEME_CORPORATE_BLUE);
  } else if (purpose === 'educate') {
    suggestions.push(THEME_CLASSROOM, THEME_SCIENCE, THEME_MODERN_LIGHT);
  } else if (purpose === 'inform') {
    suggestions.push(THEME_CONSULTING, THEME_MODERN_LIGHT, THEME_MINIMAL);
  } else if (purpose === 'entertain') {
    suggestions.push(THEME_CREATIVE, THEME_NEON, THEME_PLAYFUL);
  } else if (purpose === 'persuade') {
    suggestions.push(THEME_CONFERENCE, THEME_CORPORATE_BLUE, THEME_STARTUP);
  }

  // Remove duplicates and return top 3
  const unique = [...new Set(suggestions)];
  return unique.slice(0, 3);
}

/**
 * Resolve a theme from a user hint (e.g., "dark", "corporate", "neon", "midnight")
 * Tries: direct ID match → name match → tag match → category match
 */
export function resolveThemeFromHint(hint: string): StyleTheme | undefined {
  if (!hint) return undefined;
  const h = hint.toLowerCase().trim();

  // 1. Direct ID match
  const byId = getThemeById(h);
  if (byId) return byId;

  // 2. Name match (case-insensitive)
  const byName = ALL_THEMES.find((t) => t.name.toLowerCase() === h);
  if (byName) return byName;

  // 3. Tag match
  const byTag = ALL_THEMES.find((t) => t.tags?.includes(h));
  if (byTag) return byTag;

  // 4. Category mapping (synonyms → categories)
  const categoryMap: Record<string, StyleTheme['category']> = {
    dark: 'dark',
    darker: 'dark',
    light: 'light',
    lighter: 'light',
    corporate: 'professional',
    business: 'professional',
    formal: 'professional',
    professional: 'professional',
    colorful: 'colorful',
    fun: 'colorful',
    simple: 'minimal',
    clean: 'minimal',
    minimal: 'minimal',
  };
  const mappedCategory = categoryMap[h];
  if (mappedCategory) {
    const byCategory = ALL_THEMES.find((t) => t.category === mappedCategory);
    if (byCategory) return byCategory;
  }

  return undefined;
}

/**
 * Default theme to use when no preference specified
 */
export const DEFAULT_THEME = THEME_CLEAN;
